// Contract of the Power admin routes for OPIN applications: the confidential service-account
// clients of the OPIN realm a participant's systems call the receiver APIs with. Keycloak is the
// registry; power lists them, creates them, rotates their secret, renames them and enables or
// disables them. A `builtin` application is the realm's own and is listed read-only. No body
// carries a scope: an application always has the template's journey scopes and never an operator
// one, so a request naming a scope is refused like any other key the contract does not define.
//
// The structs are closed contracts: decode them with `{ onExcessProperty: 'error' }`.
import { Schema } from 'effect';
import { FAPI_HEADERS, IsoDateTimeUtc } from '@finnest/core/effect';

/** Keycloak's own bound on a client id, name and description. */
const KEYCLOAK_TEXT_MAX = 255;
const NAME_MAX = 100;
/** The most applications a list answers: far above any participant's integrations. */
export const OPIN_APPLICATIONS_CAP = 500;

/** `participant`: managed in power-admin; `builtin`: declared by the realm, read-only. */
export const OpinApplicationKind = Schema.Literals(['participant', 'builtin']);
export type OpinApplicationKind = typeof OpinApplicationKind.Type;

/** The OAuth `client_id` an application authenticates with. */
export const OpinApplicationClientId = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(KEYCLOAK_TEXT_MAX),
);
export type OpinApplicationClientId = typeof OpinApplicationClientId.Type;

/**
 * The `x-idempotency-key` header of a create or a rotation: a UUID the screen draws once per
 * intended operation. The same key again replays nothing (409); with another request, 422.
 */
export const OpinApplicationIdempotencyKey = Schema.String.check(Schema.isUUID());
export type OpinApplicationIdempotencyKey = typeof OpinApplicationIdempotencyKey.Type;

/** The headers of a create or a rotation. */
export const OpinApplicationCommandHeaders = Schema.Struct({
  [FAPI_HEADERS.idempotencyKey]: OpinApplicationIdempotencyKey,
});

/**
 * The `detail` of each refusal of a create or rotation by its idempotency key, the one wording
 * Power answers and the screen matches on: the key's request was done already, is being done,
 * failed without making anything (start again with a new key), failed after Keycloak may have
 * acted on it (check the applications before starting again), another rotation of the
 * application is being done (each 409), or the key was used for another request (422). None
 * carries a secret.
 */
export const OPIN_APPLICATION_COMMAND_REFUSALS = {
  alreadyProcessed: 'already processed — generate a new secret if you did not keep it',
  inProgress: 'already being processed under this idempotency key',
  attemptFailed:
    'a previous attempt with this idempotency key failed; nothing was created or changed — start again',
  outcomeUnknown:
    'a previous attempt with this idempotency key may have created or changed the application — check the applications before starting again',
  rotationInProgress: 'another secret rotation of this application is in progress',
  keyReused: 'this idempotency key was used for another request',
} as const;

/**
 * The 503 `detail` Power answers when the realm holds as many applications as one list answers
 * (`OPIN_APPLICATIONS_CAP`): no more can be created, and more could not be listed.
 */
export const OPIN_APPLICATIONS_OVER_CAP_DETAIL =
  'as many opin applications as one list answers: no more can be created, and more cannot be listed';

/** What the operator calls an application. */
export const OpinApplicationName = Schema.String.check(
  Schema.isTrimmed(),
  Schema.isMinLength(1),
  Schema.isMaxLength(NAME_MAX),
);

/** What the application is for; the empty text clears it. */
export const OpinApplicationDescription = Schema.String.check(
  Schema.isMaxLength(KEYCLOAK_TEXT_MAX),
);

const StoredText = Schema.String.check(Schema.isMaxLength(KEYCLOAK_TEXT_MAX));

/**
 * An application as listed. `name` and `description` are absent when Keycloak holds none;
 * `createdAt` only for an application power created. Never a secret.
 */
export const OpinApplicationView = Schema.Struct({
  clientId: OpinApplicationClientId,
  name: Schema.optionalKey(StoredText),
  description: Schema.optionalKey(StoredText),
  enabled: Schema.Boolean,
  kind: OpinApplicationKind,
  createdAt: Schema.optionalKey(IsoDateTimeUtc),
}).annotate({
  title: 'PowerAdminOpinApplication',
  description: 'An OPIN application: a confidential service-account client of the OPIN realm.',
});
export type OpinApplicationView = typeof OpinApplicationView.Type;

export const OpinApplicationList = Schema.Struct({
  applications: Schema.Array(OpinApplicationView).check(Schema.isMaxLength(OPIN_APPLICATIONS_CAP)),
}).annotate({
  title: 'PowerAdminOpinApplicationList',
  description: 'Every participant and built-in OPIN application, participant ones first.',
});
export type OpinApplicationList = typeof OpinApplicationList.Type;

/** Body of the create route; the client id is generated, the scopes are the template's. */
export const OpinApplicationCreateBody = Schema.Struct({
  name: OpinApplicationName,
  description: Schema.optionalKey(OpinApplicationDescription),
}).annotate({
  title: 'PowerAdminOpinApplicationCreate',
  description: 'The name and optional description of a new participant application.',
});
export type OpinApplicationCreateBody = typeof OpinApplicationCreateBody.Type;

/** Body of the edit route: the name, the description, or both. */
export const OpinApplicationUpdateBody = Schema.Struct({
  name: Schema.optionalKey(OpinApplicationName),
  description: Schema.optionalKey(OpinApplicationDescription),
})
  .check(
    Schema.makeFilter(
      (body) =>
        body.name !== undefined ||
        body.description !== undefined ||
        'expected a name or description',
    ),
  )
  .annotate({
    title: 'PowerAdminOpinApplicationUpdate',
    description: 'The new name, description, or both, of a participant application.',
  });
export type OpinApplicationUpdateBody = typeof OpinApplicationUpdateBody.Type;

/**
 * An application with the client secret it was just issued. Answered once, by the create and
 * rotate routes, with `Cache-Control: no-store`; power keeps no copy and never shows it again.
 */
export const OpinApplicationSecretIssued = Schema.Struct({
  application: OpinApplicationView,
  clientSecret: Schema.String.check(Schema.isMinLength(1)),
}).annotate({
  title: 'PowerAdminOpinApplicationSecretIssued',
  description: 'An application and its new client secret, shown once.',
});
export type OpinApplicationSecretIssued = typeof OpinApplicationSecretIssued.Type;
